import { ChangeEvent, useEffect, useLayoutEffect, useState } from 'react';
import { Box, Chip, Grid, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { formatCurrency, formatPhoneNumber } from '../formattingUtils';
import {
  useGetApprovedAmount,
  useGetConsumer,
  useApplyStartOver,
  useSetSelectedTab,
  useGetApprovingLender,
  useGetIsApprovedAmountEstimated,
  useGetCheckoutMinimumAmount,
  useGetFinancialProductType,
} from '../services/flowManager';
import {
  useGetMerchantConfigurationOptions,
  useGetMerchantFullName,
  useGetShowCartToggle,
} from '../services/configManager';
import { saveToUserSession } from '../services/userSessionStorageManager';
import { ApprovingLenderLogo } from '../pages/main';
import ApplyButton from '../components/ApplyButton';
import SendLinkButton from '../components/SendLinkButton';
import { TextField, VerticalSpace } from '@chargeafter/ui';
import { addToContext, removeFromContext } from '../services/contextManager';
import {
  SELF_FINANCING_PRODUCT,
  cartTotalErrorMessage,
  ButtonName,
} from '../constants';
import { Keyboard } from '../components/Keyboard';
import Switch from '@mui/material/Switch';
import { AdvancedCart } from '../components/AdvancedCart';
import { CartTotal } from '../components/CartTotal';
import {
  useGetMerchantOrderId,
  useIsCartOverBooked,
  useSetManualMerchantOrderId,
  useCartSingleItem,
  useSetSingleItemName,
  useGetSingleItemName,
  useCartTypeToggle,
  useSingleLenderCartAmountError,
} from '../services/cartManager';
import {
  MuiTrackerButton,
  TrackerIconButton,
} from '../components/TrackerButtonWrapper';
import { LinkFlow } from '../services/sendLinkManager';
import CheckoutButton from '../components/CheckoutButton';
import { useCheckoutConfig } from './CartTab/hooks/useCheckoutConfig';
import { useApplyConfig } from './CartTab/hooks/useApplyConfig';
import {
  StyledGridItem,
  StyledCartGrid,
  StyledCartHeader,
  StyledTypography,
  StyledOrderIdTextField,
  PaymentsContainer,
  StyledPaymentOption,
  StyledButtonWrapper,
  StyledMinimumRibbon,
  StyledOutlinedBox,
  StyledCustomerInnerDetailsBox,
  StyledDivider,
  StyledSingleCartContainer,
} from './CreateCartTab.styled';
import { ValidationError } from 'yup';
import { isCartValid } from '../validations';

const CreateCartTab = () => {
  return (
    <Grid container>
      <StyledGridItem item sm={8.5}>
        <CreateCart />
      </StyledGridItem>
      <StyledGridItem item sm={3.5}>
        <PaymentSection />
      </StyledGridItem>
    </Grid>
  );
};

export default CreateCartTab;

const CreateCart = () => {
  const [cartSingleItem] = useCartSingleItem();
  const [toggleStatus, setToggleStatus] = useCartTypeToggle();

  const [total, setTotal] = useState(cartSingleItem[0]?.price.toString());
  const cartToggle = useGetShowCartToggle();
  const { merchantOrderIdRequired } = useGetMerchantConfigurationOptions();
  const handleToggle = () => {
    saveToUserSession('switchToAdvancedCart', !toggleStatus);
    setToggleStatus(!toggleStatus);
  };

  useEffect(() => {
    if (!cartSingleItem.length) {
      setTotal(cartSingleItem[0]?.price.toString() ?? '');
    }
  }, [cartSingleItem]);

  const handleKeyClick = (keyValue: string) => {
    switch (keyValue) {
      case 'delete':
        if (total) {
          if (total[total.length - 1] === '.') {
            setTotal(total.slice(0, -2));
          } else {
            setTotal(total.slice(0, -1));
          }
        }
        return;
      case '.':
        if (!total.includes('.')) {
          setTotal(`${total}.`);
        }
        return;
      default:
        if (
          (total.includes('.') && total.split('.')[1].length < 2) ||
          (!total.includes('.') &&
            (isNaN(parseFloat(total)) ? 0 : parseFloat(total)) < 100000)
        ) {
          if (keyValue === '0') {
            if (total) {
              setTotal(`${total}${keyValue}`);
            }
          } else {
            setTotal(`${total}${keyValue}`);
          }
        }
        return;
    }
  };

  return (
    <StyledCartGrid>
      <VerticalSpace units={4} />
      <StyledCartHeader>
        <StyledTypography data-testid="cart-title" variant="body4">
          Cart {`${toggleStatus ? 'Items' : 'Details'} `}
        </StyledTypography>
        {cartToggle && (
          <Box>
            <Switch
              data-testid="advanced-cart-switch"
              checked={toggleStatus}
              onChange={handleToggle}
            />
            <StyledTypography variant="body4">Advanced Cart</StyledTypography>
          </Box>
        )}
      </StyledCartHeader>

      {toggleStatus ? (
        <AdvancedCart />
      ) : (
        <>
          <StyledSingleCartContainer>
            <CartDetailsSingleItem total={total} />
            {merchantOrderIdRequired && (
              <>
                <MerchantOrderIdField />
              </>
            )}
          </StyledSingleCartContainer>
          <Keyboard handleKeyClick={handleKeyClick} />
        </>
      )}
    </StyledCartGrid>
  );
};

const MerchantOrderIdField = () => {
  const updateMerchantOrderId = useSetManualMerchantOrderId();
  const [error, setError] = useState<string | null>(null);

  const merchantOrderId = useGetMerchantOrderId();
  const handleMerchantOrderIdChange = (newOrderId: string) => {
    if (error) {
      cartValidationHandler(
        {
          merchantOrderId: newOrderId,
          merchantOrderIdRequired: true,
        },
        'merchantOrderId',
        setError,
      );
    }
    saveToUserSession('merchantOrderId', newOrderId);
    updateMerchantOrderId(newOrderId);
  };

  const handleBlur = () =>
    cartValidationHandler(
      { merchantOrderId, merchantOrderIdRequired: true },
      'merchantOrderId',
      setError,
    );

  return (
    <StyledOrderIdTextField
      inputProps={{
        'data-testId': 'merchant-order-id',
      }}
      placeholder="Add Order Id"
      onChange={(e) => handleMerchantOrderIdChange(e.target.value)}
      value={merchantOrderId ?? ''}
      onBlur={handleBlur}
      error={!!error}
      helperText={error}
    />
  );
};

const PaymentSection = () => {
  const consumer = useGetConsumer();
  const { consumersTab, pendingTab } = useGetMerchantConfigurationOptions();

  const { isApplyAllowed, shouldShowApplySendLink, shouldShowApplySection } =
    useApplyConfig();
  const {
    isCheckoutAllowed,
    isExpressCheckoutAllowed,
    shouldShowCheckoutButton,
    shouldShowCheckoutSendLink,
  } = useCheckoutConfig();

  return (
    <PaymentsContainer>
      <StyledPaymentOption>
        {isApplyAllowed && (
          <StyledTypography variant="body4">
            {!consumer ? 'Application Options' : 'Consumer Details'}
          </StyledTypography>
        )}

        {shouldShowApplySection ? (
          <Box>
            {(isApplyAllowed || shouldShowApplySendLink) && (
              <>
                <Typography variant="body4">New Customer</Typography>
                <Grid container spacing={2}>
                  {isApplyAllowed && (
                    <Grid item sm={12}>
                      <ApplyButton />
                    </Grid>
                  )}
                  {shouldShowApplySendLink && (
                    <Grid item sm={12}>
                      <SendLinkButton action={LinkFlow.Apply} />
                    </Grid>
                  )}
                </Grid>
                <VerticalSpace units={4} />
              </>
            )}
            {(consumersTab || pendingTab) && isCheckoutAllowed && (
              <SelectExistingConsumerButton />
            )}
          </Box>
        ) : (
          <CustomerDetailsNoItems />
        )}
      </StyledPaymentOption>

      {(isCheckoutAllowed || isExpressCheckoutAllowed) && (
        <StyledPaymentOption>
          <StyledTypography variant="body4">Checkout options</StyledTypography>
          <Grid container spacing={2}>
            {shouldShowCheckoutSendLink && (
              <Grid item sm={12}>
                <SendLinkButton
                  action={
                    !consumer ? LinkFlow.ExpressCheckout : LinkFlow.Checkout
                  }
                />
              </Grid>
            )}
            {shouldShowCheckoutButton && (
              <Grid item sm={12}>
                <CheckoutButton />
              </Grid>
            )}
          </Grid>
        </StyledPaymentOption>
      )}
    </PaymentsContainer>
  );
};

const SelectExistingConsumerButton = () => {
  const updateSelectedTab = useSetSelectedTab();
  const { consumersTab } = useGetMerchantConfigurationOptions();
  const handleSelectExistingConsumerClick = () => {
    const selectedApp = !consumersTab ? 'pending' : 'customers';
    updateSelectedTab(selectedApp);
  };
  return (
    <>
      <Typography variant="body4">Existing Customer</Typography>
      <MuiTrackerButton
        buttonName={ButtonName.SelectConsumerNavigation}
        onClick={handleSelectExistingConsumerClick}
        color="primary"
        fullWidth
        data-testid="select-existing-consumer-btn"
      >
        <StyledButtonWrapper>
          <Typography variant="inherit">Select Customer</Typography>
        </StyledButtonWrapper>
      </MuiTrackerButton>
    </>
  );
};

const cartValidationHandler = (
  values: Record<string, unknown>,
  fieldName: string,
  errorSetter: (value: React.SetStateAction<string | null>) => void,
) => {
  const isValid = isCartValid({ ...values }, fieldName);

  if (isValid instanceof ValidationError) {
    errorSetter(isValid.message);
  } else errorSetter(null);
};

const CartDetailsSingleItem = ({ total }: { total: string }) => {
  const [error, setError] = useState<string | null>(null);
  const setSingleItemName = useSetSingleItemName();
  const merchantConfigOptions = useGetMerchantConfigurationOptions();
  const minimumAmount = useGetCheckoutMinimumAmount();
  const overBooked = useIsCartOverBooked();
  const [cartSingleItem, setSingleItem] = useCartSingleItem();
  const approvedAmount = useGetApprovedAmount();
  const itemName = useGetSingleItemName();
  const financingProductType = useGetFinancialProductType();
  const merchantName = useGetMerchantFullName();
  const singleLenderCartAmountError = useSingleLenderCartAmountError();
  const setTotal = (itemName: string, price?: number) => {
    const singleItem = price
      ? [
          {
            name: itemName || `${merchantName} Service`,
            quantity: 1,
            price,
            sku: Date.now().toString(),
          },
        ]
      : [];
    setSingleItem(singleItem);
    saveToUserSession('cartItems', singleItem);
    addToContext('cart', singleItem);
    if (!singleItem.length) removeFromContext('cart');
  };

  useLayoutEffect(() => {
    setTotal(itemName, parseFloat(total) ?? 0);
  }, [total, itemName]);

  const handelOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const itemName = event.target.value;
    if (error) {
      cartValidationHandler(
        { singleItemName: itemName },
        'singleItemName',
        setError,
      );
    }
    saveToUserSession('itemName', itemName);
    setSingleItemName(itemName);
  };

  const handleBlur = () =>
    cartValidationHandler(
      { singleItemName: itemName },
      'singleItemName',
      setError,
    );

  const selfFinance = financingProductType === SELF_FINANCING_PRODUCT;

  const cartErrorMessage = !!(approvedAmount && overBooked && !selfFinance)
    ? cartTotalErrorMessage.amountExceeding
    : singleLenderCartAmountError;

  return (
    <>
      {merchantConfigOptions?.description && (
        <>
          <TextField
            data-testid="description-input"
            value={itemName}
            onChange={handelOnChange}
            onBlur={handleBlur}
            label="Add Description (Optional)"
            error={!!error}
            helperText={error}
          />
        </>
      )}
      <Box>
        <CartTotal
          showError={!!cartErrorMessage}
          errorMessage={cartErrorMessage}
          totalAmount={cartSingleItem[0]?.price ?? ''}
        />
        {!!minimumAmount && (+total < minimumAmount || isNaN(+total)) && (
          <StyledMinimumRibbon data-testid="minimum-ribbon">
            <Typography variant="body1" fontWeight="bold">
              Minimum cart total {formatCurrency(minimumAmount)}
            </Typography>
          </StyledMinimumRibbon>
        )}
      </Box>
    </>
  );
};

const CustomerDetailsNoItems = () => {
  const consumer = useGetConsumer();
  const approvedAmount = useGetApprovedAmount();
  const applyStartOver = useApplyStartOver();
  const approvingLender = useGetApprovingLender();
  const isApprovedAmountEstimated = useGetIsApprovedAmountEstimated();
  const financingProductType = useGetFinancialProductType();

  return (
    <StyledOutlinedBox p={3}>
      <TrackerIconButton
        buttonName={ButtonName.RemoveConsumer}
        sx={{ float: 'right', padding: 0 }}
        onClick={() => {
          applyStartOver();
        }}
      >
        <CloseIcon />
      </TrackerIconButton>
      <StyledCustomerInnerDetailsBox>
        <Typography
          variant="body2"
          data-testid="customer-name"
        >{`${consumer?.firstName} ${consumer?.lastName}`}</Typography>
        <Typography variant="body2">
          {formatPhoneNumber(consumer?.mobilePhoneNumber ?? '')}
        </Typography>
      </StyledCustomerInnerDetailsBox>
      {!!approvedAmount || !!approvingLender ? (
        <>
          <StyledDivider />
          <Box textAlign="center">
            {!!approvingLender && <ApprovingLenderLogo />}

            {financingProductType !== SELF_FINANCING_PRODUCT &&
              !!approvedAmount && (
                <>
                  <Box paddingBottom={2} />
                  <Typography variant="body2">Available Amount</Typography>
                  <Typography
                    variant="h2"
                    color="primary"
                    data-testid="approved-loan"
                  >
                    {formatCurrency(approvedAmount)}
                  </Typography>
                  {isApprovedAmountEstimated && (
                    <>
                      <Chip variant="grey" label="Estimated" />
                    </>
                  )}
                </>
              )}
          </Box>
        </>
      ) : null}
    </StyledOutlinedBox>
  );
};
